import type { ReactNode } from "react";
import {
  useGetIdentity,
  useGetList,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";

import { formatDateOnly } from "../clients/clientModel";
import type { Client, ContentPiece } from "../types";
import { StatusPill } from "../calendar/PieceDetail";
import { FORMAT_ICONS, STATUS_COLORS, toDateKey } from "../calendar/pieceModel";

const DAY_MS = 24 * 60 * 60 * 1000;

// Dashboard of a team member: their own pieces grouped by what to do next.
export const TeamDashboard = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const today = new Date();
  const inAWeek = new Date(today.getTime() + 7 * DAY_MS);

  const { data: pieces = [], isPending } = useGetList<ContentPiece>(
    "content_pieces",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "publish_date", order: "ASC" },
      filter: {
        assignee_id: identity?.id,
        "status@neq": "published",
      },
    },
    { enabled: identity?.id != null },
  );
  const { data: clients = [] } = useGetList<Client>("clients", {
    pagination: { page: 1, perPage: 500 },
  });
  const clientName = (id: unknown) =>
    clients.find((client) => String(client.id) === String(id))?.name ?? "";

  const byStatus = (status: ContentPiece["status"]) =>
    pieces.filter((piece) => piece.status === status);
  const toPublish = byStatus("approved").filter(
    (piece) =>
      piece.publish_date != null && piece.publish_date <= toDateKey(inAWeek),
  );
  const waiting = byStatus("in_review").sort((a, b) =>
    (a.review_requested_at ?? "").localeCompare(b.review_requested_at ?? ""),
  );

  const firstName = String(identity?.fullName ?? "").split(" ")[0];

  return (
    <div className="mt-6 flex flex-col gap-6 pb-8">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">
          {translate("crm.agency_dashboard.greeting", { name: firstName })}
        </h1>
        <p className="mt-1 text-muted-foreground">
          {translate("crm.my_work.subtitle")}
        </p>
      </div>

      {isPending ? null : (
        <div className="grid gap-4 lg:grid-cols-2">
          <WorkSection
            title={translate("crm.my_work.changes")}
            empty={translate("crm.my_work.changes_empty")}
            color={STATUS_COLORS.changes_requested}
            pieces={byStatus("changes_requested")}
            clientName={clientName}
            urgent
          />
          <WorkSection
            title={translate("crm.my_work.to_do")}
            empty={translate("crm.my_work.to_do_empty")}
            color={STATUS_COLORS.preparing}
            pieces={byStatus("preparing")}
            clientName={clientName}
          />
          <WorkSection
            title={translate("crm.my_work.waiting")}
            empty={translate("crm.my_work.waiting_empty")}
            color={STATUS_COLORS.in_review}
            pieces={waiting}
            clientName={clientName}
            detail={(piece) => <WaitingDays piece={piece} />}
          />
          <WorkSection
            title={translate("crm.my_work.to_publish")}
            empty={translate("crm.my_work.to_publish_empty")}
            color={STATUS_COLORS.approved}
            pieces={toPublish}
            clientName={clientName}
            detail={(piece) => <PublishDay piece={piece} />}
          />
        </div>
      )}
    </div>
  );
};

// Shown on the agency dashboard: assigning owners is an administrator task.
export const UnassignedPieces = () => {
  const translate = useTranslate();
  const { data: pieces = [] } = useGetList<ContentPiece>("content_pieces", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "publish_date", order: "ASC" },
    filter: {
      "assignee_id@is": "null",
      "status@in": "(preparing,changes_requested)",
    },
  });
  const { data: clients = [] } = useGetList<Client>("clients", {
    pagination: { page: 1, perPage: 500 },
  });
  if (pieces.length === 0) return null;
  const clientName = (id: unknown) =>
    clients.find((client) => String(client.id) === String(id))?.name ?? "";
  return (
    <WorkSection
      title={translate("crm.my_work.unassigned")}
      empty=""
      color="var(--muted-foreground)"
      pieces={pieces}
      clientName={clientName}
    />
  );
};

const WorkSection = ({
  title,
  empty,
  color,
  pieces,
  clientName,
  detail,
  urgent = false,
}: {
  title: string;
  empty: string;
  color: string;
  pieces: ContentPiece[];
  clientName: (id: unknown) => string;
  detail?: (piece: ContentPiece) => ReactNode;
  urgent?: boolean;
}) => (
  <section
    className={`rounded-[22px] bg-card p-5 ${
      urgent && pieces.length > 0 ? "ring-1 ring-[#f5a524]/60" : ""
    }`}
  >
    <h2 className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      <span className="size-2 rounded-full" style={{ background: color }} />
      {title}
      <span className="ml-auto text-base font-semibold text-foreground normal-case tracking-normal">
        {pieces.length}
      </span>
    </h2>
    {pieces.length === 0 ? (
      <p className="text-sm text-muted-foreground">{empty}</p>
    ) : (
      <ul className="flex flex-col gap-2">
        {pieces.map((piece) => (
          <PieceRow
            key={piece.id}
            piece={piece}
            clientName={clientName(piece.company_id)}
            detail={detail?.(piece)}
          />
        ))}
      </ul>
    )}
  </section>
);

const PieceRow = ({
  piece,
  clientName,
  detail,
}: {
  piece: ContentPiece;
  clientName: string;
  detail?: ReactNode;
}) => {
  const [locale = "es"] = useLocaleState();
  const Icon = FORMAT_ICONS[piece.format];
  const month = piece.publish_date?.slice(0, 7);
  const to = `/calendar?client=${piece.company_id}${
    month ? `&month=${month}` : ""
  }&piece=${piece.id}`;

  return (
    <li>
      <Link
        to={to}
        className="flex items-center gap-3 rounded-2xl bg-secondary px-4 py-3 text-foreground no-underline hover:bg-accent"
      >
        <Icon className="size-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">{piece.title}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {clientName}
            {piece.publish_date
              ? ` · ${formatDateOnly(piece.publish_date, locale)}`
              : ""}
          </span>
        </span>
        {detail ?? <StatusPill status={piece.status} />}
      </Link>
    </li>
  );
};

const WaitingDays = ({ piece }: { piece: ContentPiece }) => {
  const translate = useTranslate();
  const since = piece.review_requested_at ?? piece.updated_at;
  const days = Math.max(
    0,
    Math.floor((Date.now() - new Date(since).getTime()) / DAY_MS),
  );
  return (
    <span
      className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
        days >= 3
          ? "bg-[#f5a524]/15 text-[#f5a524]"
          : "bg-background text-muted-foreground"
      }`}
    >
      {days === 0
        ? translate("crm.my_work.sent_today")
        : translate("crm.my_work.waiting_days", { smart_count: days })}
    </span>
  );
};

const PublishDay = ({ piece }: { piece: ContentPiece }) => {
  const translate = useTranslate();
  const todayKey = toDateKey(new Date());
  const label =
    piece.publish_date === todayKey
      ? translate("crm.my_work.today")
      : piece.publish_date != null && piece.publish_date < todayKey
        ? translate("crm.my_work.overdue")
        : null;
  if (!label) return <StatusPill status={piece.status} />;
  return (
    <span className="shrink-0 rounded-full bg-[#3dd68c]/15 px-2.5 py-1 text-xs font-medium text-[#3dd68c]">
      {label}
    </span>
  );
};
